import type {
  AccountUserLicenseTypeValue,
  SuggestedAccountUser,
  SuggestedAccountUserGroup,
} from '@frameio/graphql-client/generated';
import { isValidEmail } from '@frameio/utils/validators';
import type { InputToken } from '../../../utils';
import { InputTokenStatus } from '../../../utils';
import type { SelectedMember } from '../../../utils/SelectedMemberType';
import { STATUS_TOOLTIP } from '../memberAccessFormConstants';
import { unpackMaybe } from './unpackMaybe';

const getUserInputTokenStatus = (
  user: SuggestedAccountUser,
  selectedLicenseType: AccountUserLicenseTypeValue,
  isManagedByAdobe: boolean
) => {
  const { email, inviteEmailAddress, licenseType } = user;

  if (!isValidEmail(email || inviteEmailAddress || '')) {
    return InputTokenStatus.INVALID_EMAIL;
  }

  const invitingAsGuest = selectedLicenseType === 'GUEST';
  const isPending = user.isPending && !user.__typename;

  if (isPending) {
    if (isManagedByAdobe) {
      return InputTokenStatus.NEW_MEMBER_WARN;
    }

    return invitingAsGuest
      ? InputTokenStatus.NEW_GUEST
      : InputTokenStatus.NEW_MEMBER;
  }

  switch (licenseType) {
    case 'MEMBER':
      return invitingAsGuest
        ? InputTokenStatus.EXISTING_MEMBER
        : InputTokenStatus.MEMBER;
    case 'GUEST':
      return invitingAsGuest
        ? InputTokenStatus.EXISTING_GUEST
        : InputTokenStatus.NEW_MEMBER;
    default:
      return InputTokenStatus.MEMBER;
  }
};

export const convertUserOrGroupToInputToken = (
  userOrGroup: SelectedMember,
  licenseType: AccountUserLicenseTypeValue = 'MEMBER',
  isManagedByAdobe = false
): InputToken => {
  const group =
    userOrGroup.__typename === 'SuggestedAccountUserGroup' &&
    (userOrGroup as SuggestedAccountUserGroup);

  if (group) {
    const status =
      licenseType === 'GUEST'
        ? InputTokenStatus.EXISTING_GROUP
        : InputTokenStatus.GROUP;
    return {
      id: group.id,
      label: unpackMaybe(group.name, ''),
      emoji: unpackMaybe(group.emoji),
      status,
      memberCount: unpackMaybe(group.memberCount),
      tooltip: STATUS_TOOLTIP[status]?.(
        group.name ?? '',
        unpackMaybe(group.memberCount)
      ),
    };
  }

  const user = userOrGroup as SuggestedAccountUser;
  const tokenLabel = user.name || user.email || user.inviteEmailAddress;
  const tokenStatus = getUserInputTokenStatus(
    user,
    licenseType,
    isManagedByAdobe
  );

  return {
    id: user.id,
    label: unpackMaybe(tokenLabel, ''),
    avatarBackground: unpackMaybe(user?.avatarColor),
    avatarSrc: unpackMaybe(user?.profileImage?.image64),
    status: tokenStatus,
    tooltip: STATUS_TOOLTIP[tokenStatus]?.(tokenLabel ?? ''),
  };
};
